import { useCallback, useEffect, useRef, useState } from "react";
import { createAgentBridge } from "./agentBridge";
import { getAuthToken } from "./aiChatClient";

let agentMsgCounter = 0;
function nextAgentMsgId() {
  agentMsgCounter += 1;
  return `agent-${Date.now()}-${agentMsgCounter}`;
}

/**
 * Manages a live support-agent session over the EMR media server.
 * Status: "idle" | "waiting" | "connected" | "disconnected".
 */
export default function useAgentBridge({ patientId, patient }) {
  const [status, setStatus] = useState("idle");
  const [agentName, setAgentName] = useState(null);
  const [messages, setMessages] = useState([]);
  const bridgeRef = useRef(null);

  const teardown = useCallback(() => {
    if (bridgeRef.current) {
      bridgeRef.current.disconnect();
      bridgeRef.current = null;
    }
  }, []);

  useEffect(() => teardown, [teardown]);

  const appendAgentMessage = useCallback((text, name) => {
    setMessages((prev) => [
      ...prev,
      { id: nextAgentMsgId(), sender: "agent", text, agentName: name, timestamp: new Date() },
    ]);
  }, []);

  const startSession = useCallback(
    (issue) => {
      if (bridgeRef.current) return;

      const bridge = createAgentBridge({
        patientId: String(patientId),
        token: getAuthToken(),
        patient: patient && !Array.isArray(patient) ? patient : { id: patientId },
      });
      bridgeRef.current = bridge;

      bridge.onStatus(({ status: s, agentName: name }) => {
        if (s === "waiting") setStatus("waiting");
        if (s === "disconnected" || s === "error") setStatus("disconnected");
        if (s === "no_agents") {
          setStatus("waiting");
          appendAgentMessage(
            "All our support agents are offline right now. You can leave your message and an agent will follow up, or continue with Anna.",
            "System",
          );
        }
        if (s === "agent_connected") {
          setStatus("connected");
          if (name) setAgentName(name);
        }
      });

      bridge.onMessage(({ message, agentName: name }) => {
        appendAgentMessage(message, name);
        if (name) setAgentName(name);
        setStatus("connected");
      });

      bridge.connect();
      bridge.requestAgent(issue);
      setStatus("waiting");
      setMessages([
        {
          id: nextAgentMsgId(),
          sender: "system",
          text: "Connecting you to a support agent… You'll be notified as soon as an agent picks up.",
          timestamp: new Date(),
        },
      ]);
      if (issue) {
        // Show the patient's opening issue in the transcript.
        setMessages((prev) => [
          ...prev,
          { id: nextAgentMsgId(), sender: "user", text: issue, timestamp: new Date() },
        ]);
      }
    },
    [patientId, patient, appendAgentMessage],
  );

  const sendMessage = useCallback((text) => {
    const trimmed = (text || "").trim();
    if (!trimmed || !bridgeRef.current) return false;

    setMessages((prev) => [
      ...prev,
      { id: nextAgentMsgId(), sender: "user", text: trimmed, timestamp: new Date() },
    ]);

    // Delivered immediately if an agent is assigned; otherwise buffered by the
    // bridge and flushed on pickup. The banner already communicates the wait.
    bridgeRef.current.sendMessage(trimmed);
    return true;
  }, []);

  const endSession = useCallback(() => {
    teardown();
    setStatus("idle");
    setAgentName(null);
    setMessages([]);
  }, [teardown]);

  return { status, agentName, messages, startSession, sendMessage, endSession };
}
